import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Box, IconButton, TextField, Alert } from "@mui/material";
import { Download, Upload } from "lucide-react";
import RefreshButton from "../../lib/components/RefreshButton";
import DataTable from "../../lib/components/DataTable";
import useLogListReducer from "./hooks/useLogList/useLogList";
import useLogDownloadReducer from "./hooks/useLogDownload/useLogDownloadHook";
import useLatestLogsReducer from "./hooks/useLatestLogs/useLatestLogsHook";
import {
  DEFAULT_DOWNLOAD_FILENAME,
  DOWNLOAD_RESET_DELAY,
  LOG_HEADER_SX,
  LOG_ALERT_SX,
  LOG_REQUEST_BTN_SX,
  LOG_DOWNLOAD_BTN_SX,
} from "./constants";
import { formatFileDate, validateDateFormat, extractTimestamp, newLogoIconVisibility } from "./utils";
import newLogIcon from "../../assets/latest_log_logo.png";

const LogManager = ({ serialNumber }) => {
  // State
  const [logFiles, setLogFiles] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [shouldFetchLatestLogs, setShouldFetchLatestLogs] = useState(false);
  const [fetchStartDate, setFetchStartDate] = useState(null);
  const [fetchEndDate, setFetchEndDate] = useState(null);
  const [showLogRequestInfoMessage, setShowLogRequestInfoMessage] = useState();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);

  const logListKey = serialNumber ? `${serialNumber}_${refreshTrigger}` : null;
  const logList = useLogListReducer(logListKey);
  const logDownloadDetails = useLogDownloadReducer(serialNumber, fileName);
  useLatestLogsReducer(shouldFetchLatestLogs ? serialNumber : null, fetchStartDate, fetchEndDate);

  // Memoized data
  const logListData = useMemo(() => {
    return logList?.state?.data || logList?.state?.status?.data;
  }, [logList?.state?.data, logList?.state?.status?.data]);

  const downloadData = useMemo(() => {
    return logDownloadDetails?.state?.data;
  }, [logDownloadDetails?.state?.data]);

  // Download handler
  const triggerDownload = useCallback((presignedUrl, fileNameToDownload) => {
    const link = document.createElement("a");
    link.href = presignedUrl;
    link.download = fileNameToDownload || DEFAULT_DOWNLOAD_FILENAME;
    link.rel = "noopener noreferrer";
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (!downloadData || !fileName) return;

    if (typeof downloadData === "string" && downloadData.trim()) {
      triggerDownload(downloadData, fileName);
      // Reset fileName after download to allow re-download
      setTimeout(() => setFileName(null), DOWNLOAD_RESET_DELAY);
    }
  }, [downloadData, fileName, triggerDownload]);

  // sorting logic
  useEffect(() => {
    if (!Array.isArray(logListData) || logListData.length === 0) {
      setLogFiles([]);
      return;
    }

    const sortedAndLimited = [...logListData].sort((a, b) => extractTimestamp(b) - extractTimestamp(a)).slice(0, 10);

    setLogFiles(sortedAndLimited);
  }, [logListData]);

  // Generic date change handler
  const handleDateChange = useCallback(
    field => e => {
      const value = e.target.value;
      const setDate = field === "start" ? setStartDate : setEndDate;
      const setError = field === "start" ? setStartDateError : setEndDateError;

      setDate(value);

      if (value.trim() === "") {
        setError("");
      } else if (!validateDateFormat(value)) {
        setError("Date must be in YYYY-MM-DD format (e.g., 2025-12-02)");
      } else {
        setError("");
      }
    },
    []
  );

  // Validate if both dates are filled and valid
  const isFetchEnabled = useMemo(() => {
    const dates = [
      { value: startDate, error: startDateError },
      { value: endDate, error: endDateError },
    ];

    return dates.every(({ value, error }) => value.trim() !== "" && !error && validateDateFormat(value));
  }, [startDate, endDate, startDateError, endDateError]);

  const handleFetchClick = () => {
    if (isFetchEnabled) {
      setFetchStartDate(startDate);
      setFetchEndDate(endDate);
      setShouldFetchLatestLogs(true);
      setShowLogRequestInfoMessage(true);
      setShowExportModal(false);
      setTimeout(() => {
        setShowLogRequestInfoMessage(false);
      }, 15000);
      setTimeout(() => {
        setFetchStartDate("");
        setFetchEndDate("");
      }, 0);
      setStartDate("");
      setEndDate("");
    }
  };

  const handleExportModalClose = () => {
    setShowExportModal(false);
    setStartDate("");
    setEndDate("");
    setStartDateError("");
    setEndDateError("");
  };

  const handleDownload = fileName => {
    setFileName(fileName);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      sortable: false,
      render: (value, row, rowIndex) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{formatFileDate(row.fileName) || "—"}</span>
          {rowIndex === 0 && newLogoIconVisibility(row.fileName) && (
            <img src={newLogIcon} alt="new log" width={25} height={25} style={{ display: "block" }} />
          )}
        </div>
      ),
    },
    {
      key: "fileName",
      label: "File Name",
      sortable: false,
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filterable: false,
      width: "100px",
      render: (value, row) => (
        <IconButton onClick={() => handleDownload(row.fileName)} sx={LOG_DOWNLOAD_BTN_SX} size="small">
          <Download size={20} />
        </IconButton>
      ),
    },
  ];

  const tableRows = useMemo(() => {
    return logFiles.map((file, index) => ({
      index: index,
      fileName: file,
      date: formatFileDate(file) || "",
      download: null,
    }));
  }, [logFiles]);

  return (
    <Box>
      <Box sx={LOG_HEADER_SX}>
        <Box>
          {showLogRequestInfoMessage && (
            <Alert variant="outlined" severity="info" sx={LOG_ALERT_SX}>
              Fetching latest logs might take up to 5 minutes
            </Alert>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowExportModal(true)}
            startIcon={<Upload size={15} />}
            sx={LOG_REQUEST_BTN_SX}
          >
            Request Charger Logs
          </Button>
          <RefreshButton onClick={handleRefresh} disabled={!serialNumber} tooltip="Refresh logs" />
        </Box>
      </Box>

      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold mb-4">Request Charger Logs</h2>
            <div className="space-y-4">
              <div>
                <TextField
                  label="Start Date"
                  placeholder="YYYY-MM-DD"
                  value={startDate}
                  onChange={handleDateChange("start")}
                  error={!!startDateError}
                  helperText={startDateError}
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                />
              </div>
              <div>
                <TextField
                  label="End Date"
                  placeholder="YYYY-MM-DD"
                  value={endDate}
                  onChange={handleDateChange("end")}
                  error={!!endDateError}
                  helperText={endDateError}
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-5">
              <button
                type="button"
                onClick={handleExportModalClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFetchClick}
                disabled={!isFetchEnabled}
                className="px-4 py-2 btn-accent rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Request Logs
              </button>
            </div>
          </div>
        </div>
      )}

      <DataTable columns={columns} data={tableRows} isLoading={logList?.state?.isLoading} />
    </Box>
  );
};

LogManager.propTypes = {
  serialNumber: PropTypes.string,
};

export default LogManager;
